import { Button } from '@creditornot/cb-button';
import { typographyCss, cssThemedValue } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { WoltModal } from 'common/components';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';
import { useOfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';

import { dietaryPreferenceTranslationMap, sharedMenuMandatoryWarningTranslationMap } from '../../constants';
import { OptionValue } from '../../types';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  optionChoices: OptionValue[];
  primaryLanguage: Language;
};

const Root = styled(WoltModal)`
  max-height: 90vh;
  max-width: 1000px;
  width: 100%;
  border-radius: 8px;
  background: ${cssThemedValue('surfaceMain')};
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

const Content = styled(ModalContent)`
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 24px;
`;

const Section = styled.div`
  & + & {
    margin-block-start: 24px;
  }
`;

const SectionTitle = styled.span`
  ${typographyCss.Title1};
  display: block;
  margin-block-end: 16px;
`;

const Fields = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-inline-start: -8px;
  margin-inline-end: -8px;
  margin-block-end: -8px;
`;

const FormField = styled.div`
  flex: 0 0 50%;
  padding-inline-start: 8px;
  padding-inline-end: 8px;
  margin-block-end: 8px;
  ${typographyCss.Body3}
`;

const Label = styled.label`
  ${typographyCss.Small};
  display: block;
  margin-block-end: 4px;
`;

const Footer = styled(ModalFooter)`
  justify-content: flex-end;
`;

export const OptionChoiceAdvancedDetailsViewModal = ({ isOpen, onClose, optionChoices, primaryLanguage }: Props) => {
  const intl = useIntl();
  const menuFeatures = useOfferingMenuFeaturesContext();

  return (
    <Root isOpen={isOpen} onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="menu-option.advanced-details" />
      </ModalHeader>
      <Content>
        {optionChoices.map(optionChoice => (
          <Section key={optionChoice.id}>
            <SectionTitle>{localize(optionChoice.name, primaryLanguage)}</SectionTitle>
            <Fields>
              <FormField>
                <Label>
                  <FormattedMessage id="menu.ingredients" />
                </Label>
                <T.Body3>{optionChoice.ingredients?.find(t => t.lang === primaryLanguage)?.value || '-'}</T.Body3>
              </FormField>
              <FormField>
                <Label>
                  <FormattedMessage id="menu.additives" />
                </Label>
                <T.Body3>{optionChoice.additives?.find(t => t.lang === primaryLanguage)?.value || '-'}</T.Body3>
              </FormField>
              <FormField>
                <Label>
                  <FormattedMessage id="menu.nutrition-facts" />
                </Label>
                <T.Body3>{optionChoice.nutrition_facts?.find(t => t.lang === primaryLanguage)?.value || '-'}</T.Body3>
              </FormField>
              <FormField>
                <Label>
                  <FormattedMessage id="menu.allergens" />
                </Label>
                <T.Body3>{optionChoice.allergens?.find(t => t.lang === primaryLanguage)?.value || '-'}</T.Body3>
              </FormField>
              <FormField>
                <Label>
                  <FormattedMessage id="menu.producer-information" />
                </Label>
                <T.Body3>
                  {optionChoice.producer_information?.find(t => t.lang === primaryLanguage)?.value || '-'}
                </T.Body3>
              </FormField>
              <FormField>
                <Label>
                  <FormattedMessage id="menu.distributor-information" />
                </Label>
                <T.Body3>
                  {optionChoice.distributor_information?.find(t => t.lang === primaryLanguage)?.value || '-'}
                </T.Body3>
              </FormField>
              <FormField>
                <Label>
                  <FormattedMessage id="menu.dietaryPreferences.title" />
                </Label>
                {optionChoice.dietary_preferences && optionChoice.dietary_preferences.length > 0
                  ? optionChoice.dietary_preferences
                      .map(type => intl.formatMessage({ id: dietaryPreferenceTranslationMap[type] }))
                      .join(', ')
                  : '-'}
              </FormField>
              {menuFeatures.mandatoryWarnings && (
                <FormField>
                  <Label>
                    <FormattedMessage id="menu.mandatoryWarnings.title" />
                  </Label>
                  {optionChoice.mandatory_warnings && optionChoice.mandatory_warnings.length > 0
                    ? optionChoice.mandatory_warnings
                        .map(warning => intl.formatMessage({ id: sharedMenuMandatoryWarningTranslationMap[warning] }))
                        .join(', ')
                    : '-'}
                </FormField>
              )}
            </Fields>
          </Section>
        ))}
      </Content>
      <Footer>
        <Button size="small" variant="blue" onClick={onClose}>
          <FormattedMessage id="state.close" />
        </Button>
      </Footer>
    </Root>
  );
};
